import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, SectionHead, ServiceTile, Tabs, Icon, Skeleton, EmptyState, Button, Badge } from '../../components/ui';
import { servicesService } from '../../services';

const OUR_SERVICES = [
  { icon: 'video', title: 'Live Puja', desc: 'Participate in live puja from anywhere', comingSoon: true },
  { icon: 'package', title: 'Pooja Kit at Home', desc: 'Get authentic pooja kit delivered to your home', comingSoon: true },
  { icon: 'namaste', title: 'Pandit Ji Booking', desc: 'Book experienced pandit ji for puja', to: '/pandits' },
  { icon: 'diya', title: 'E Puja / Online Puja', desc: 'Book and perform puja online with ease', to: '/epuja' },
  { icon: 'heart-handshake', title: 'Seva & Donation', desc: 'Contribute to temples and noble causes', to: '/seva' },
  { icon: 'ticket', title: 'VIP Darshan', desc: 'Skip the lines & get special darshan experience', comingSoon: true },
  { icon: 'gift', title: 'Prasad Delivery', desc: 'Get temple prasad delivered to your home', comingSoon: true },
  { icon: 'store', title: 'Gift & Souvenir', desc: 'Religious items & souvenirs for your loved ones', comingSoon: true },
];

const phoneText = (contact) => {
  if (!contact?.phone) return null;
  return Array.isArray(contact.phone) ? contact.phone.join(', ') : contact.phone;
};

export default function Services() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const selected = params.get('category') || 'All';

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    servicesService.getAllServices()
      .then((data) => { if (active) setServices(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(services.map((s) => s.category).filter(Boolean)))], [services]);
  const activeCat = categories.includes(selected) ? selected : 'All';
  const filtered = activeCat === 'All' ? services : services.filter((s) => s.category === activeCat);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === 'All') next.delete('category'); else next.set('category', c);
    setParams(next, { replace: true });
  };

  return (
    <>
      <SEO
        title="Our Services"
        description="E-puja, pandit ji booking, seva and on-the-ground pilgrim services in Ayodhya — transport, food, stays and essentials."
        canonicalUrl="/services"
        keywords="Ayodhya services, pilgrim services Ayodhya, puja booking, pandit booking, prasad delivery"
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Our Services') }]} title={t('Our Services')} subtitle={t('Everything you need for a blessed journey')} />

      <div className="container listing-wrap">
        <div className="grid-4" style={{ marginBottom: 48 }}>
          {OUR_SERVICES.map((s) => (
            <ServiceTile key={s.title} icon={s.icon} title={t(s.title)} desc={t(s.desc)} to={s.to} comingSoon={!!s.comingSoon} soonLabel={t('Coming Soon')} />
          ))}
        </div>

        <SectionHead eyebrow={t('On the ground')} title={t('Pilgrim Services in Ayodhya')} subtitle={t('Transport, food, stays and essentials to help you navigate Ayodhya Dham')} />

        {categories.length > 1 && (
          <div className="listing__toolbar">
            <Tabs variant="pill" items={categories.map((c) => ({ id: c, label: t(c) }))} value={activeCat} onChange={setCategory} ariaLabel={t('Service category')} />
          </div>
        )}

        {loading ? (
          <div className="card-grid card-grid--wide">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={240} radius="var(--r-lg)" />)}</div>
        ) : error ? (
          <EmptyState icon="circle-alert" title={t('Could not load services')} body={t('The server is not reachable right now. Please try again.')} action={<Button variant="outline" onClick={() => setReload((k) => k + 1)}>{t('Retry')}</Button>} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="handshake" title={t('No services found')} body={t('Try another category.')} />
        ) : (
          <div className="card-grid card-grid--wide">
            {filtered.map((s) => (
              <article key={s.id} className="card info-card">
                <div className="info-card__head">
                  <h3 className="info-card__title">{s.name}</h3>
                  {s.verified && <Icon name="badge-check" size={20} style={{ color: 'var(--primary)', flex: 'none' }} title={t('Verified')} />}
                </div>
                <div className="info-card__tags">
                  {s.category && <Badge variant="soft">{s.category}</Badge>}
                  {s.type && <Badge variant="outline">{s.type}</Badge>}
                  {s.is_featured && <Badge variant="gold">{t('Featured')}</Badge>}
                </div>
                {s.description && <p className="info-card__desc">{s.description}</p>}
                <ul className="info-list">
                  {s.location?.address && (
                    <li><span className="info-list__icon"><Icon name="map-pin" size={14} /></span><span>{s.location.address}{s.location.distance_from_ram_mandir ? ` · ${s.location.distance_from_ram_mandir} ${t('from Ram Mandir')}` : ''}</span></li>
                  )}
                  {s.pricing?.price_range && (
                    <li><span className="info-list__icon"><Icon name="indian-rupee" size={14} /></span><span>{s.pricing.price_range}</span></li>
                  )}
                  {s.operating_hours?.monday && (
                    <li><span className="info-list__icon"><Icon name="clock" size={14} /></span><span>{s.operating_hours.monday}</span></li>
                  )}
                  {phoneText(s.contact) && (
                    <li><span className="info-list__icon"><Icon name="phone" size={14} /></span><a href={`tel:${String(phoneText(s.contact)).split(',')[0].replace(/[^+\d]/g, '')}`}>{phoneText(s.contact)}</a></li>
                  )}
                </ul>
              </article>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
